<template>
  <div class="card card-body client-details-page">

    <!-- LOADING -->
    <div v-if="loading || !client" class="text-center py-5 text-muted">
      <b-spinner class="me-2" /> Loading client details...
    </div>

    <div v-else>
      <!-- HEADER -->
      <section
        class="mb-4 d-flex flex-column flex-md-row align-items-center align-items-md-start text-center text-md-start">

        <img :src="passportPhotoDisplayUrl ? apiBaseUrl + passportPhotoDisplayUrl : defaultPhoto"
          class="rounded-circle border border-secondary shadow-sm me-3"
          style="width: 10rem; height: 10rem; object-fit: cover;" />

        <div>
          <h3 class="text-primary fw-bold">{{ client.fullName || 'N/A' }}</h3>

          <p class="text-muted mb-0"><i class="bi bi-envelope me-1"></i>{{ client.email || 'No email' }}</p>
          <p class="text-muted mb-0"><i class="bi bi-phone me-1"></i>{{ client.phoneNumber || 'No phone' }}</p>

          <p class="text-muted mb-0">
            <i class="bi bi-briefcase me-1"></i>
            Officer:
            <span class="fw-bold ms-1">{{ client.officerName || 'N/A' }}</span>
          </p>

          <p class="text-muted mb-0">
            <i class="bi bi-briefcase me-1"></i>
            Member Number
            <span class="fw-bold ms-1">{{ client.memberNumber || 'N/A' }}</span>
          </p>


          <p class="text-muted mb-0">
            <i class="bi bi-building me-1"></i>
            Station:
            <span class="fw-bold ms-1">{{ client.stationName || 'N/A' }}</span>
          </p>

          <p class="text-muted">
            <i class="bi bi-people me-1"></i>Member of:

            <button v-if="client.groupId" class="btn btn-sm btn-outline-primary ms-2"
              @click="openGroupPage(client.groupId)">
              {{ client.groupName || 'N/A' }}
              <i class="bi bi-eye"></i>
            </button>

            <span v-else>N/A</span>
          </p>
        </div>
      </section>

      <!-- PERSONAL DETAILS -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Personal Details</h5>

        <b-row class="g-3">
          <b-col md="3">
            <strong>ID Number:</strong>
            <div class="text-muted">{{ client.idNumber || 'N/A' }}</div>
          </b-col>

          <b-col md="3">
            <strong>Date of Birth:</strong>
            <div class="text-muted">{{ formatDob(client.dob) }}</div>
          </b-col>

          <b-col md="3">
            <strong>Gender:</strong>
            <div class="text-muted">{{ client.gender || 'N/A' }}</div>
          </b-col>

          <b-col md="3">
            <strong>Monthly Savings:</strong>
            <div class="text-muted">{{ client.monthlySavingsAmount
              ? formatCurrency(client.monthlySavingsAmount) : 'N/A' }}</div>
          </b-col>
        </b-row>
      </section>

      <!-- LOCATION DETAILS -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Location Details</h5>

        <b-table :items="client.locations" :fields="locationFields" small responsive bordered striped show-empty
          empty-text="No location details found.">
          <template #cell(type)="data">
            <span v-if="data.index === 0">Residential Address</span>
            <span v-else-if="data.index === 1">Home Address</span>
            <span v-else>Location {{ data.index + 1 }}</span>
          </template>

          <template #cell(gps)="data">
            <span v-if="data.item.gps">{{ data.item.gps }}</span>
            <span v-else class="text-muted">N/A</span>
          </template>
        </b-table>
      </section>

      <!-- DOCUMENTS -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Documents</h5>

        <b-row>
          <b-col md="6">
            <strong>Passport Photo:</strong>
            <div>
              <a v-if="passportPhotoDisplayUrl" :href="apiBaseUrl + passportPhotoDisplayUrl" target="_blank">View
                Photo</a>
              <span v-else class="text-muted">Not uploaded</span>
            </div>
          </b-col>

          <b-col md="6">
            <strong>ID Document:</strong>
            <div>
              <a v-if="idDocumentDisplayUrl" :href="apiBaseUrl + idDocumentDisplayUrl" target="_blank">View Document</a>
              <span v-else class="text-muted">Not uploaded</span>
            </div>
          </b-col>
        </b-row>

        <div v-if="otherDocuments.length" class="mt-3">
          <h6>Other Documents:</h6>

          <b-table :items="otherDocuments" :fields="otherDocumentFields" small responsive striped>
            <template #cell(fileUrl)="data">
              <a :href="apiBaseUrl + data.item.fileUrl" target="_blank">View</a>
            </template>
          </b-table>
        </div>
      </section>

      <!-- ACCOUNTS -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Financial Accounts</h5>

        <b-row>
          <!-- Savings -->
          <b-col md="6">
            <b-card class="h-100">
              <b-card-header class="bg-light fw-bold">💰 Savings & Current Accounts</b-card-header>
              <b-table :items="savingsAndCurrentAccounts" :fields="accountFields" small bordered striped responsive
                show-empty empty-text="No savings or current accounts.">
                <template #cell(balance)="data">
                  <a href="#" class="text-success fw-semibold" @click.prevent="viewLedger(data.item)">
                    {{ formatCurrency(data.item.balance) }}
                  </a>
                </template>

              </b-table>
            </b-card>
          </b-col>

          <!-- Loan Accounts -->
          <b-col md="6">
            <b-card class="h-100">
              <b-card-header class="bg-light fw-bold">🧾 Loan Accounts</b-card-header>
              <b-table :items="loanAccounts" :fields="accountFields" small bordered striped responsive show-empty
                empty-text="No loan accounts.">
                <template #cell(name)="data">
                  {{ getLoanName(data.item.name) }}
                </template>

                <template #cell(balance)="data">
                  <a href="#" class="text-danger fw-semibold" @click.prevent="viewLedger(data.item)">
                    {{ formatCurrency(data.item.balance) }}
                  </a>
                </template>

              </b-table>

            </b-card>
          </b-col>
        </b-row>
      </section>

      <!-- NEXT OF KIN -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Next of Kin</h5>

        <b-table :items="client.nextOfKins" :fields="kinFields" small bordered striped responsive show-empty
          empty-text="No next of kin details found." />
      </section>

      <!-- ACTION BUTTONS -->
      <div class="d-flex justify-content-end mt-4">
        <button class="btn btn-success me-2" @click="applyForLoan">
          <i class="bi bi-cash-stack me-1"></i> Apply for Loan
        </button>

        <!-- <button class="btn btn-primary" @click="editClient">
          <i class="bi bi-pencil-square me-1"></i> Edit Client
        </button> -->
      </div>

    </div>
  </div>
</template>

<script>
import groupApi from "@/api/groupApi";
import clientApi from "@/api/clientApi";

export default {
  name: "ClientDetailsPage",

  data() {
    return {
      client: null,
      loading: false,
      apiBaseUrl: "",
      defaultPhoto: "https://via.placeholder.com/100?text=Photo",

      locationFields: [
        { key: "type", label: "Type" },
        { key: "county", label: "County" },
        { key: "subCounty", label: "Sub-County" },
        { key: "ward", label: "Ward" },
        { key: "gps", label: "GPS" }
      ],

      accountFields: [
        { key: "name", label: "Account Name" },
        { key: "balance", label: "Balance" }
      ],

      kinFields: [
        { key: "name", label: "Full Name" },
        { key: "relationship", label: "Relationship" },
        { key: "phone", label: "Phone Number" }
      ],

      otherDocumentFields: [
        { key: "fileName", label: "File Name" },
        { key: "contentType", label: "Type" },
        { key: "fileUrl", label: "Action" }
      ]
    };
  },

  computed: {
    passportPhotoDisplayUrl() {
      const doc = this.client?.documents?.[0];
      return doc?.fileUrl || this.client?.passportPhotoDisplayUrl || null;
    },
    idDocumentDisplayUrl() {
      const doc = this.client?.documents?.[1];
      return doc?.fileUrl || this.client?.idDocumentDisplayUrl || null;
    },
    otherDocuments() {
      return (this.client?.documents || []).slice(2);
    },
    savingsAndCurrentAccounts() {
      return (this.client?.accountSummaries || []).filter(a =>
        ["SAVING", "CURRENT"].includes(a.accountCategory?.toUpperCase())
      );
    },
    loanAccounts() {
      return (this.client?.accountSummaries || []).filter(a => {
        const cat = a.accountCategory?.toUpperCase();
        return cat && !["SAVING", "CURRENT"].includes(cat);
      });
    }
  },

  created() {
    this.loading = true;

    const saved = localStorage.getItem("clientToView");

    if (saved) {
      // Admin/officer flow
      this.client = JSON.parse(saved);
      this.loading = false;

      localStorage.removeItem("clientToView");
    } else {
      // Client self-dashboard flow
      this.fetchMyProfile();
    }
  },

  methods: {
    viewLedger(acc) {
      this.$router.push({
        name: "LedgerSummary",
        params: { accountId: acc.id }
      });
    },
    getLoanName(accountName) {
      if (!accountName) return "N/A";

      const parts = accountName.split("Loan:");
      return parts.length > 1 ? parts[1].trim() : accountName;
    },
    async fetchMyProfile() {
      try {
        const res = await clientApi.getMyProfile();
        this.client = res.data;
      } catch (e) {
        console.error("Failed to load client profile", e);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.back();
    },

    async openGroupPage(groupId) {
      const res = await groupApi.getById(groupId);
      localStorage.setItem("groupToView", JSON.stringify(res.data));
      localStorage.setItem("clickedmenu", "View Group");
      this.$router.push("/display");
    },

    formatDob(dob) {
      if (!dob) return "N/A";
      try {
        return new Date(dob).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric"
        });
      } catch {
        return dob;
      }
    },

    formatCurrency(amount) {
      if (amount == null) return "KES 0.00";
      return `KES ${Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },

    editClient() {
      this.$router.push({
        name: "Entry",
        params: { clientId: this.client.id },
        query: { menu: "New Client" }
      });
    },

    applyForLoan() {
      this.$router.push({
        name: "forms_entry",
        query: {
          idNumber: this.client.idNumber,
          prefill: true,
          menu: "New Loan"
        }
      });
    }
  }
};
</script>

<style scoped>
.client-details-page a {
  text-decoration: none;
}

.client-details-page a:hover {
  text-decoration: underline;
  opacity: 0.85;
}

section {
  padding-bottom: 1rem;
}
</style>
